import { useEffect, useRef, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConfigContext } from '../../../contexts/ConfigContext';
// react-bootstrap
import Image from 'react-bootstrap/Image';

import DrawerContent from './DrawerContent';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { useRestaurantLogos } from '../../../APIs/Settings/general';
import useAuth from '../../../auth/useAuth';

function getInitials(name) {
  if (!name) return '?';
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

export default function MainDrawer() {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster?.isDashboardDrawerOpened;
  const [selectedItems, setSelectedItems] = useState();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  const overlayRef = useRef(null);
  const { menuOrientation, sidebarTheme } = useConfig();
  const location = useLocation();
  const navigate = useNavigate();
  const config = useContext(ConfigContext);
  const { user, meLoading } = useAuth();

  const userId = !meLoading ? user?.user?.id : null;
  const { logos: logoData, isLoading: loadingLogos, refresh } = useRestaurantLogos(userId);
  const [logos, setLogos] = useState({
    light: '',
    dark: '',
    favicon: ''
  });
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current?.contains(event.target)) {
        handlerDrawerOpen(false);
      }
    };
    if (isMobile) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobile]);
  useEffect(() => {
    if (logoData) {
      setLogos({
        light: logoData.light,
        dark: logoData.dark,
        favicon: logoData.favicon
      });
    }
  }, [logoData]);
  const isLargeScreen = window.innerWidth > 1024;

  useEffect(() => {
    const removeClasses = ['layout-2', 'layout-3', 'preset-1', 'preset-brand-1'];
    const resetAttributes = () => {
      document.body.classList.remove(...removeClasses);
    };

    resetAttributes();

    const pathname = location.pathname;

    if (!isLargeScreen) {
      document.body.setAttribute('data-pc-layout', 'vertical');
    }

    if (pathname === '/layouts/layout-2') {
      document.body.removeAttribute('data-pc-layout');
      document.body.setAttribute('data-pc-preset', 'preset-1');
      document.body.classList.add('layout-2', 'preset-1');
    }

    if (pathname === '/layouts/layout-3') {
      document.body.removeAttribute('data-pc-layout');
      document.body.setAttribute('data-pc-preset', 'preset-1');
      document.body.classList.add('layout-3', 'preset-brand-1');
    }

    // always run layout logic
    switch (menuOrientation) {
      case MenuOrientation.TAB:
      case MenuOrientation.VERTICAL:
        document.body.setAttribute('data-pc-layout', menuOrientation.toLowerCase());
        break;
      case MenuOrientation.LAYOUT2:
        document.body.setAttribute('data-pc-layout', MenuOrientation.VERTICAL);
        document.body.setAttribute('data-pc-preset', 'preset-1');
        document.body.classList.add('layout-2', 'preset-1');
        break;
      case MenuOrientation.LAYOUT3:
        document.body.setAttribute('data-pc-layout', MenuOrientation.VERTICAL);
        document.body.setAttribute('data-pc-preset', 'preset-1');
        document.body.classList.add('layout-3', 'preset-brand-1');
        break;
      default:
        break;
    }
  }, [menuOrientation, isLargeScreen, location.pathname]);

  const userName = user?.user?.name || '';
  const restaurantName = user?.restaurant?.name || '';
  const planName = user?.subscription?.plan_name || 'Free';
  const agreementSigned = user?.restaurant?.agreement_signed ?? true;
  const isMerchantAdmin = user?.user?.user_type === 'merchant_admin';
  const showAgreementBadge = isMerchantAdmin && !agreementSigned;
  const showSignedBadge = isMerchantAdmin && agreementSigned;
  const displayName = restaurantName || userName;

  return (
    <nav id="pc-sidebar" className={`pc-sidebar ${drawerOpen ? 'pc-sidebar-hide mob-sidebar-active' : ''} `}>
      <div className="navbar-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <div className="m-header" style={{ flexShrink: 0 }}>
          <a className="b-brand text-primary">
            <div className="b-bg ">
              <Image src={config.mode === 'dark' ? logos?.dark : logos?.light} fluid className="app-logo" alt="logo" />
            </div>
          </a>
        </div>

        <div
          className={menuOrientation === MenuOrientation.TAB ? 'tab-container' : 'navbar-content'}
          style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
        >
          <DrawerContent selectedItems={selectedItems} setSelectedItems={setSelectedItems} />
        </div>

        {/* Sidebar profile widget */}
        <div
          className={`sidebar-profile-widget${showAgreementBadge ? ' sidebar-profile-widget--pending' : ''}${showSignedBadge ? ' sidebar-profile-widget--signed' : ''}`}
          onClick={isMerchantAdmin ? () => navigate('/merchant-agreement') : undefined}
          title={
            showAgreementBadge
              ? 'Merchant agreement pending — click to sign'
              : showSignedBadge
                ? 'View signed merchant agreement'
                : undefined
          }
        >
          {/* Merchant admins get the outlined avatar in both agreement states —
              signing shouldn't swap the icon for an initials disc. Other user
              types keep the initials. */}
          <div className="sidebar-profile-avatar">
            {showAgreementBadge || showSignedBadge ? (
              <i className="ph ph-user-circle" style={{ fontSize: 20, lineHeight: 1 }} />
            ) : (
              getInitials(userName)
            )}
          </div>
          <div className="sidebar-profile-info">
            <span className="sidebar-profile-name" title={displayName}>
              {displayName}
            </span>
            <span className="sidebar-profile-plan">{planName}</span>
          </div>
          {showAgreementBadge && <span className="sidebar-agreement-badge">Pending</span>}
          {showSignedBadge && <span className="sidebar-agreement-badge sidebar-agreement-badge--accepted">Terms Accepted</span>}
        </div>
      </div>
      {drawerOpen && isMobile && <div className="pc-menu-overlay" ref={overlayRef} />}
    </nav>
  );
}
